import { useEffect, useState } from "react";
import { isAuthSetupDebugEnabled } from "../lib/authSetupDebug";
import { CheckCircle, CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronUp, Globe, Lock, Zap, Sparkles, RefreshCw } from "lucide-react";
import { detectAuthType } from "./AuthConfigStep";
import { baseUrlInputPlaceholder } from "../lib/baseUrl";
import BaseUrlConfigStep from "./BaseUrlConfigStep";
import AuthOverrideStep from "./AuthOverrideStep";
import AuthConfigStep from "./AuthConfigStep";
import AuthDetectionBanner from "./AuthDetectionBanner";
import OAuthConnectButton from "./OAuthConnectButton";

const SUCCESS_GREEN_BG = "#EAF3DE";
const SUCCESS_GREEN = "#3B6D11";
const SUCCESS_GREEN_TEXT = "#27500A";

// ─── Demo data (remove in production) ────────────────────────────────────────
export const DEMO_DATA = {
  format: "Postman Collection v2.1",
  base_url: "https://api.example.com",
  auth: { type: "bearer", header: "Authorization" },
  schema_warning_counts: { suppressed: 0, low: 0, medium: 0 },
  endpoints: [
    { method: "GET",    path: "/users",          description: "List all users",        status: "ok" },
    { method: "POST",   path: "/users",           description: "Create a new user",     status: "ok" },
    { method: "GET",    path: "/users/{id}",      description: "Fetch user by ID",      status: "ok" },
    { method: "PUT",    path: "/users/{id}",      description: "Update user",           status: "warning", issue: "Missing param type for 'id'" },
    { method: "DELETE", path: "/users/{id}",      description: "Delete user",           status: "error",   issue: "No auth detected" },
    { method: "GET",    path: "/posts",           description: "List posts",            status: "ok" },
    { method: "POST",   path: "/posts",           description: "Create post",           status: "warning", issue: "Request body schema is empty" },
  ],
};

// ─── Method badge ─────────────────────────────────────────────────────────────
const METHOD_STYLES = {
  GET:    { bg: "#E6F1FB", color: "#0C447C" },
  POST:   { bg: "#EAF3DE", color: "#27500A" },
  PUT:    { bg: "#FAEEDA", color: "#633806" },
  PATCH:  { bg: "#EEEDFE", color: "#3C3489" },
  DELETE: { bg: "#FCEBEB", color: "#791F1F" },
};

function MethodBadge({ method }) {
  const s = METHOD_STYLES[method] || { bg: "#F1EFE8", color: "#444441" };
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 500, letterSpacing: "0.04em",
      padding: "2px 8px", borderRadius: 6, flexShrink: 0,
      fontFamily: "monospace",
    }}>
      {method}
    </span>
  );
}

// ─── Status icon ──────────────────────────────────────────────────────────────
function StatusIcon({ status }) {
  if (status === "ok")      return <CheckCircle  size={15} color={SUCCESS_GREEN} />;
  if (status === "warning") return <AlertTriangle size={15} color="#854F0B" />;
  return                           <XCircle      size={15} color="#A32D2D" />;
}

// ─── Single endpoint row ──────────────────────────────────────────────────────
function EndpointRow({ ep }) {
  const borderColor = ep.status === "error" ? "#F7C1C1" : ep.status === "warning" ? "#FAC775" : "#e8e6e0";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 14px", borderRadius: 10,
      background: "rgba(255,255,255,0.7)",
      border: `1px solid ${borderColor}`,
      backdropFilter: "blur(8px)",
    }}>
      <MethodBadge method={ep.method} />
      <span style={{ fontFamily: "monospace", fontSize: 13, color: "var(--color-text-primary)", flexShrink: 0 }}>
        {ep.path}
      </span>
      <span style={{ fontSize: 13, color: "var(--color-text-secondary)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {ep.description || <em style={{ opacity: 0.5 }}>no description</em>}
      </span>
      <StatusIcon status={ep.status} />
    </div>
  );
}

function GroupedEndpointList({ groups }) {
  const [expanded, setExpanded] = useState({});

  function toggle(name) {
    setExpanded(prev => ({ ...prev, [name]: !prev[name] }));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {groups.map((group) => (
        <div key={group.group_name}>
          {/* Group header row */}
          <div
            onClick={() => toggle(group.group_name)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 14px", borderRadius: expanded[group.group_name] ? "10px 10px 0 0" : 10,
              background: "rgba(255,255,255,0.7)",
              border: "1px solid #e8e6e0",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            {/* Arrow */}
            <span style={{ fontSize: 11, color: "var(--color-text-secondary)", flexShrink: 0 }}>
              {expanded[group.group_name] ? "▼" : "▶"}
            </span>

            {/* Group name */}
            <span style={{
              fontFamily: "monospace", fontSize: 13,
              fontWeight: 500, color: "var(--color-text-primary)", flex: 1
            }}>
              {group.group_name}
            </span>

            {/* Count badge */}
            <span style={{
              background: "#EAF3DE", color: "#27500A",
              fontSize: 11, fontWeight: 500,
              padding: "2px 8px", borderRadius: 20, flexShrink: 0,
            }}>
              {group.endpoint_count} endpoint{group.endpoint_count !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Expanded endpoints */}
          {expanded[group.group_name] && (
            <div style={{
              border: "1px solid #e8e6e0",
              borderTop: "none",
              borderRadius: "0 0 10px 10px",
              overflow: "hidden",
            }}>
              {group.endpoints.map((ep, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "8px 14px",
                    background: i % 2 === 0 ? "rgba(255,255,255,0.5)" : "rgba(245,245,245,0.5)",
                    borderTop: i === 0 ? "none" : "1px solid #f0ede8",
                  }}
                >
                  <MethodBadge method={ep.method} />
                  <span style={{ fontFamily: "monospace", fontSize: 12, color: "var(--color-text-primary)", flexShrink: 0 }}>
                    {ep.path}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--color-text-secondary)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {ep.summary || ep.description || ""}
                  </span>
                  <StatusIcon status={ep.support_level === "manual" ? "error" : ep.support_level === "partial" ? "warning" : "ok"} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function NotesIssuesSection({
  errors,
  warnings,
  hasSchemaWarningCounters,
  schemaWarningCounts,
  cardStyle,
}) {
  const [open, setOpen] = useState(false);
  const total = errors.length + warnings.length;

  return (
    <div data-testid="notes-issues-slot" style={{ marginBottom: 12 }}>
      <div
        style={{
          ...cardStyle,
          marginBottom: 0,
          borderColor: errors.length ? "#F7C1C1" : "#FAC775",
          padding: 0,
          overflow: "hidden",
        }}
      >
        {/* Collapsed trigger */}
        <div
          onClick={() => setOpen(v => !v)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "13px 20px", cursor: "pointer", userSelect: "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={14} color={errors.length ? "#A32D2D" : "#854F0B"} />
            <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
              <strong style={{ color: errors.length ? "#791F1F" : "#633806" }}>
                {errors.length > 0
                  ? `${errors.length} error${errors.length !== 1 ? "s" : ""}${warnings.length > 0 ? `, ${warnings.length} warning${warnings.length !== 1 ? "s" : ""}` : ""}`
                  : `${warnings.length} warning${warnings.length !== 1 ? "s" : ""}`}
              </strong>
              {" "}detected
              {hasSchemaWarningCounters && (
                <span style={{ color: "var(--color-text-tertiary)", marginLeft: 8 }}>
                  · medium {schemaWarningCounts.medium} · low {schemaWarningCounts.low} · suppressed {schemaWarningCounts.suppressed}
                </span>
              )}
            </span>
          </div>
          {open
            ? <ChevronUp size={14} color="var(--color-text-tertiary)" />
            : <ChevronDown size={14} color="var(--color-text-tertiary)" />}
        </div>

        {/* Expanded content */}
        {open && (
          <div style={{ borderTop: `1px solid ${errors.length ? "#F7C1C1" : "#FAC775"}`, padding: "16px 20px 20px" }}>
        <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Notes & issues
        </p>

        {errors.map((ep, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
            <XCircle size={14} color="#A32D2D" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 13, color: "#791F1F" }}>
                <code style={{ background: "#FCEBEB", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>{ep.method} {ep.path}</code>
                {" — "}{ep.issue}
              </span>
              {ep.issue?.toLowerCase().includes("auth") && (
                <div style={{ marginTop: 6 }}>
                  <select style={{ fontSize: 12, padding: "3px 8px", borderRadius: 6 }}>
                    <option>bearer</option>
                    <option>apiKey</option>
                    <option>basic</option>
                    <option>none</option>
                  </select>
                </div>
              )}
              {Array.isArray(ep.autoFixes) && ep.autoFixes.length > 0 && (
                <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {ep.autoFixes.map((fix, idx) => (
                    <span
                      key={`${i}-fix-${idx}`}
                      style={{
                        fontSize: 10,
                        padding: "2px 8px",
                        borderRadius: 999,
                        border: "1px solid #DCE7F7",
                        background: "#F1F6FD",
                        color: "#2F568A",
                        lineHeight: 1.2,
                      }}
                    >
                      auto-fixed: {fix}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {warnings.map((ep, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={14} color="#854F0B" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 13, color: "#633806" }}>
                <code style={{ background: "#FAEEDA", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>{ep.method} {ep.path}</code>
                {" — "}{ep.issue}
              </span>
              {Array.isArray(ep.autoFixes) && ep.autoFixes.length > 0 && (
                <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {ep.autoFixes.map((fix, idx) => (
                    <span
                      key={`${i}-warn-fix-${idx}`}
                      style={{
                        fontSize: 10,
                        padding: "2px 8px",
                        borderRadius: 999,
                        border: "1px solid #DCE7F7",
                        background: "#F1F6FD",
                        color: "#2F568A",
                        lineHeight: 1.2,
                      }}
                    >
                      auto-fixed: {fix}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%",
  fontSize: 14,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid rgba(0,0,0,0.1)",
  boxSizing: "border-box",
  marginTop: 4,
  outline: "none",
  boxShadow: "none",
  appearance: "none",
  WebkitAppearance: "none",
};

export default function PreviewPage({
  parsedData = DEMO_DATA,
  onGenerate,
  baseUrlOverride = "",
  onBaseUrlOverrideChange,
  needsBaseUrlInput = false,
  authSchemes = [],
  credentials = {},
  onCredentialsChange,
  canSubmitSetup = false,
  isConverting = false,
  // When true, the inline yellow "Connect your API" panel inside the spec
  // card is suppressed. The host page is expected to render its own
  // credentials UI (e.g. AuthConfigStep) below.
  hideInlineSetup = false,
  showPostPreviewAuthSetup = false,
  showBaseUrlStep = false,
  showAuthOverride = false,
  showAuthConfig = false,
  showGenerateCard = false,
  overrideReason = "missing",
  authOverride = undefined,
  onAuthOverrideChange = undefined,
  credentialsSkipped = false,
  onCredentialsSkip = undefined,
  onCredentialsResume = undefined,
  baseUrlReady = true,
  authReady = true,
  needsManualOverride = false,
  authRequirement = null,
  authBannerConnected = false,
  onAuthBannerConnect = undefined,
  onAuthBannerSkip = undefined,
  onAuthBannerUseApiKey = undefined,
  hasOAuthSupport = false,
  oauthConfig = null,
  oauthConnected = false,
  onOAuthSuccess = undefined,
  onOAuthDisconnect = undefined,
}) {
  const [showAll, setShowAll] = useState(false);

  const data     = parsedData;
  const authType = detectAuthType(authSchemes || []);
  const showSetup = !hideInlineSetup && (needsBaseUrlInput || authType !== "none");

  useEffect(() => {
    if (!hideInlineSetup && !showPostPreviewAuthSetup) return;
    console.warn("[AuthSetup] PreviewPage", {
      showPostPreviewAuthSetup,
      showBaseUrlStep,
      showAuthOverride,
      showAuthConfig,
      showGenerateCard,
      hideInlineSetup,
      needsBaseUrlInput,
      authType,
      verbose: isAuthSetupDebugEnabled(),
    });
  }, [
    showPostPreviewAuthSetup,
    showBaseUrlStep,
    showAuthOverride,
    showAuthConfig,
    showGenerateCard,
    hideInlineSetup,
    needsBaseUrlInput,
    authType,
  ]);

  const total    = data.endpoints.length;
  const warnings = data.endpoints.filter(e => e.status === "warning");
  const errors   = data.endpoints.filter(e => e.status === "error");
  const hasIssues = warnings.length > 0 || errors.length > 0;
  const canGenerate = errors.length === 0;
  const visible  = showAll ? data.endpoints : data.endpoints.slice(0, 5);
  const schemaWarningCounts = data.schema_warning_counts || { suppressed: 0, low: 0, medium: 0 };
  const hasSchemaWarningCounters =
    schemaWarningCounts.suppressed > 0 ||
    schemaWarningCounts.low > 0 ||
    schemaWarningCounts.medium > 0;

  const showCredColumn = hideInlineSetup && showPostPreviewAuthSetup;

  const cardStyle = {
    background: "rgba(255,255,255,0.75)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 14,
    padding: "16px 20px",
    marginBottom: 12,
  };


  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "24px 16px", fontFamily: "var(--font-sans)" }}>

      {/* ── Header card ── */}
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: SUCCESS_GREEN_BG,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CheckCircle2 size={20} color={SUCCESS_GREEN} />
            </div>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: 13, color: SUCCESS_GREEN, fontWeight: 600 }}>
                Spec detected
              </span>
              <p style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 600, color: "var(--color-text-primary)" }}>
                {data.format}
              </p>
            </div>
          </div>
          <span
            style={{
              background: SUCCESS_GREEN_BG,
              color: SUCCESS_GREEN_TEXT,
              fontSize: 13,
              fontWeight: 600,
              padding: "4px 12px",
              borderRadius: 20,
              flexShrink: 0,
            }}
          >
            {total} endpoint{total !== 1 ? "s" : ""}
          </span>
        </div>

        {total > 40 && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            marginTop: 12,
          }}>
            <Zap size={13} color={SUCCESS_GREEN} />
            <span style={{ fontSize: 13, color: SUCCESS_GREEN_TEXT }}>
              <strong>Smart grouping enabled</strong> — {total} endpoints will be automatically grouped into focused tools for better AI performance.
            </span>
          </div>
        )}

        {showSetup && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "2px solid #FAC775",
              background: "rgba(250, 206, 117, 0.22)",
              marginLeft: -20,
              marginRight: -20,
              marginBottom: -16,
              padding: "16px 20px 20px",
              borderRadius: "0 0 14px 14px",
            }}
          >
            <p style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#633806" }}>
              Connect your API (required)
            </p>
            {needsBaseUrlInput && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#854F0B", display: "block" }}>
                  Base URL (replace placeholder)
                </label>
                <input
                  type="url"
                  placeholder={baseUrlInputPlaceholder(data.base_url)}
                  value={baseUrlOverride}
                  onChange={e => onBaseUrlOverrideChange?.(e.target.value)}
                  style={{ ...inputStyle, borderColor: "#E8A317" }}
                />
              </div>
            )}
            {authType === "basic" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block" }}>Email / Username</label>
                  <input
                    type="email"
                    placeholder="you@company.com"
                    value={credentials.username || ""}
                    onChange={e => onCredentialsChange?.({ ...credentials, username: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block" }}>API Token</label>
                  <input
                    type="password"
                    placeholder="Paste your API token"
                    value={credentials.password || ""}
                    onChange={e => onCredentialsChange?.({ ...credentials, password: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              </div>
            )}
            {(authType === "bearer" || authType === "apiKey") && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block" }}>API Token / Key</label>
                <input
                  type="password"
                  value={credentials.api_key || ""}
                  onChange={e => onCredentialsChange?.({ ...credentials, api_key: e.target.value })}
                  style={inputStyle}
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => onGenerate?.()}
              disabled={!canSubmitSetup || isConverting}
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: 15,
                fontWeight: 600,
                borderRadius: 12,
                border: "none",
                cursor: !canSubmitSetup || isConverting ? "not-allowed" : "pointer",
                opacity: !canSubmitSetup || isConverting ? 0.55 : 1,
                background: SUCCESS_GREEN,
                color: "#fff",
              }}
            >
              {isConverting ? "Generating…" : "Generate & Host MCP Server"}
            </button>
            {!canSubmitSetup && !isConverting && (
              <p style={{ fontSize: 11, color: "#854F0B", marginTop: 8, textAlign: "center" }}>
                Fill in all fields above to enable Generate
              </p>
            )}
          </div>
        )}

        <p style={{ margin: "16px 0 12px", fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Endpoints
        </p>

        {/* GROUPED VIEW */}
        {data.is_grouped && data.grouped_endpoints ? (
          <GroupedEndpointList groups={data.grouped_endpoints} />
        ) : (
          /* NORMAL VIEW */
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {visible.map((ep, i) => <EndpointRow key={i} ep={ep} />)}
            </div>
            {total > 5 && (
              <button
                onClick={() => setShowAll(v => !v)}
                style={{
                  marginTop: 10, width: "100%", background: "transparent",
                  border: "1px dashed rgba(0,0,0,0.15)", borderRadius: 8,
                  padding: "8px 0", cursor: "pointer", fontSize: 13,
                  color: "var(--color-text-secondary)", display: "flex",
                  alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                {showAll
                  ? <><ChevronUp size={14} /> Show less</>
                  : <><ChevronDown size={14} /> Show {total - 5} more</>}
              </button>
            )}
          </>
        )}
      </div>

      {hasIssues && (
        <NotesIssuesSection
          errors={errors}
          warnings={warnings}
          hasSchemaWarningCounters={hasSchemaWarningCounters}
          schemaWarningCounts={schemaWarningCounts}
          cardStyle={cardStyle}
        />
      )}

      {showCredColumn && (
        <div data-testid="auth-setup-slot" data-auth-config id="auth-setup-section" style={{ marginBottom: 12 }}>
          <div className="glass-strong rounded-2xl p-7 shadow-soft w-full">
            {authRequirement?.is_required && authRequirement.type !== "none" ? (
              <AuthDetectionBanner
                embedded
                authRequirement={authRequirement}
                isConnected={authBannerConnected}
                onConnect={onAuthBannerConnect}
                onSkip={onAuthBannerSkip}
                onUseApiKey={onAuthBannerUseApiKey}
                withBottomDivider={showBaseUrlStep || showAuthOverride || showAuthConfig}
                oauthConnectSlot={
                  hasOAuthSupport && oauthConfig ? (
                    <OAuthConnectButton
                      appearance="banner"
                      config={oauthConfig}
                      isConnected={oauthConnected}
                      onSuccess={token => onOAuthSuccess?.(token)}
                      onDisconnect={() => onOAuthDisconnect?.()}
                    />
                  ) : null
                }
              />
            ) : null}
            {showBaseUrlStep ? (
              <BaseUrlConfigStep
                embedded
                withTopDivider={Boolean(authRequirement?.is_required && authRequirement.type !== "none")}
                specBaseUrl={data.base_url}
                value={baseUrlOverride}
                onChange={url => onBaseUrlOverrideChange?.(url)}
              />
            ) : null}
            {showAuthOverride ? (
              <AuthOverrideStep
                embedded
                withTopDivider={
                  showBaseUrlStep ||
                  Boolean(authRequirement?.is_required && authRequirement.type !== "none")
                }
                reason={overrideReason}
                value={authOverride}
                onChange={mode => onAuthOverrideChange?.(mode)}
              />
            ) : null}
            {showAuthConfig ? (
              <AuthConfigStep
                embedded
                withTopDivider={
                  showBaseUrlStep ||
                  showAuthOverride ||
                  Boolean(authRequirement?.is_required && authRequirement.type !== "none")
                }
                authSchemes={authSchemes}
                credentials={credentials}
                onChange={creds => onCredentialsChange?.(creds)}
                skipped={credentialsSkipped}
                onSkip={onCredentialsSkip}
                onResume={onCredentialsResume}
              />
            ) : null}
          </div>
          {showGenerateCard ? (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => onGenerate?.()}
                disabled={isConverting || !baseUrlReady || !authReady}
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 12,
                  border: "none",
                  cursor: isConverting || !baseUrlReady || !authReady ? "not-allowed" : "pointer",
                  opacity: isConverting || !baseUrlReady || !authReady ? 0.55 : 1,
                  background: "#1a1a1a",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {isConverting ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Sparkles size={16} />
                )}
                {isConverting ? "Generating…" : "Generate & host MCP server"}
              </button>
              {!isConverting && hasOAuthSupport && !oauthConnected && !credentialsSkipped ? (
                <p style={{ fontSize: 11, color: "#854F0B", marginTop: 8, textAlign: "center" }}>
                  Connect with OAuth above to continue
                </p>
              ) : null}
              {!isConverting && needsManualOverride && !authOverride && !hasOAuthSupport ? (
                <p style={{ fontSize: 11, color: "#854F0B", marginTop: 8, textAlign: "center" }}>
                  Pick an authentication method above to continue
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

    </div>
  );
}
